<script setup lang="ts" generic="T extends string | number">
import type { HTMLAttributes } from 'vue';
import { Users, X } from 'lucide-vue-next';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { UserInfoBlock } from '@/views/Accounts/components';

import { cn } from '@/lib/utils';

import { BUTTON_VARIANTS, COMPONENT_VARIANTS } from '@/constants/component';
import { TYPE_GROUP, TYPE_USER } from '@/constants/contentType';
import { STATUS_DEFAULT } from '@/constants/status';

import type { ListBadgeVariants, SelectOption } from '@/types/components';

import type { Container } from '@/types/containers';
import type { GroupSimplified } from '@/types/groups';
import type { UserSimplified } from '@/types/users';

const props = withDefaults(
  defineProps<{
    canBeRemoved?: boolean;
    class?: HTMLAttributes['class'];
    type: ListBadgeVariants;
    data?: SelectOption<T> | null;
    group?: GroupSimplified | null;
    container?: Container | null;
    subtitle?: string;
    testId?: string;
  }>(),
  {
    canBeRemoved: false,
    class: null,
    data: null,
    type: STATUS_DEFAULT,
    title: '',
    subtitle: '',
    testId: undefined,
    group: null,
    container: null
  }
);

defineEmits<{
  (e: 'remove-option'): void;
}>();
</script>

<template>
  <Badge
    v-if="type === TYPE_USER"
    shape="rectangular"
    :variant="COMPONENT_VARIANTS.ghost"
    :class="
      cn(
        'px-2.5 py-2 min-h-13 min-w-13 text-xs',
        canBeRemoved && 'pointer-events-auto'
      )
    "
  >
    <UserInfoBlock
      user-details-class="w-3/4"
      class="relative bg-white px-2.5 py-2 h-full items-center border border-gray-200 shadow rounded-md"
      :email="(data?.data as UserSimplified).email"
      :full-name="(data?.data as UserSimplified).fullName"
      truncate
      is-small
    >
      <Button
        v-if="canBeRemoved"
        :variant="BUTTON_VARIANTS.icon"
        :test-id="`list-badge-remove-${(data?.data as UserSimplified).email}-button`"
        class="p-0 absolute right-4"
        @click="$emit('remove-option')"
      >
        <X class="w-4 h-4 shrink-0 text-gray-500" />
      </Button>
    </UserInfoBlock>
  </Badge>

  <Badge
    v-else-if="type === TYPE_GROUP"
    :test-id="props.testId"
    :variant="COMPONENT_VARIANTS.ghost"
    :title="group?.name"
    shape="rectangular"
    :class="
      cn(
        'px-2.5 py-2 min-h-13 min-w-13 text-xs',
        canBeRemoved && 'pointer-events-auto'
      )
    "
  >
    <div
      class="w-7 h-7 bg-white border border-gray-200 rounded flex justify-center items-center"
    >
      <Users class="w-4 h-4 shrink-0 stroke-gray-600 stroke-1" />
    </div>
    <div :class="cn('flex flex-col overflow-hidden', canBeRemoved && 'pr-6')">
      <p class="text-black font-semibold truncate">
        {{ group?.name }}
      </p>
      <p class="text-gray-500 truncate">
        {{ group?.description || '-' }}
      </p>
    </div>
    <Button
      v-if="canBeRemoved"
      :test-id="`list-badge-remove-${group?.name}-button`"
      :variant="BUTTON_VARIANTS.icon"
      class="p-0 absolute right-4"
      @click="$emit('remove-option')"
    >
      <X class="w-4 h-4 shrink-0 text-gray-500" />
    </Button>
  </Badge>

  <Badge
    v-else
    :variant="COMPONENT_VARIANTS.ghost"
    shape="rectangular"
    :class="
      cn('relative shadow text-gray-800 flex gap-1.5 text-sm', props.class)
    "
    :title="data?.label"
  >
    <div class="overflow-hidden pr-6">
      <p v-if="data?.label" class="font-semibold truncate">{{ data.label }}</p>
      <p v-if="subtitle" class="text-gray-500">{{ subtitle }}</p>
    </div>
    <Button
      v-if="canBeRemoved"
      :variant="BUTTON_VARIANTS.icon"
      :test-id="`list-badge-remove-${data?.label}-button`"
      class="p-0 absolute right-4"
      @click="$emit('remove-option')"
    >
      <X class="w-4 h-4 shrink-0 text-gray-500" />
    </Button>
  </Badge>
</template>
